import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import styles from "./SeriesCarousel.module.css";

const SeriesCarousel = ({ initailData }: any) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState("right");
  const [isHovered, setIsHovered] = useState(false);
  const carouselRef = useRef(null);
  const intervalRef = useRef<NodeJS.Timeout>();

  // Cleanup all intervals
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const goToSlide = useCallback(
    (newIndex: number) => {
      setDirection(newIndex > currentIndex ? "right" : "left");
      setCurrentIndex(newIndex);
    },
    [currentIndex]
  );

  const nextSlide = useCallback(() => {
    goToSlide((currentIndex + 1) % initailData.length);
  }, [currentIndex, initailData.length, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide((currentIndex - 1 + initailData.length) % initailData.length);
  }, [currentIndex, initailData.length, goToSlide]);

  // Auto-advance slides (pauses on hover)
  useEffect(() => {
    if (!isHovered) {
      intervalRef.current = setInterval(nextSlide, 5000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [nextSlide, isHovered]);

  // Animation variants
  const carouselVariants = {
    enter: (dir: string) => ({
      x: dir === "right" ? "100%" : "-100%",
      opacity: 0,
      scale: 0.9,
    }),
    center: {
      x: "0%",
      opacity: 1,
      scale: 1,
    },
    exit: (dir: string) => ({
      x: dir === "right" ? "-100%" : "100%",
      opacity: 0,
      scale: 0.9,
    }),
  };

  const slideVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <>
      <section className={styles.modernCarousel}>
        {/* Background Elements */}
        <div className={styles.backgroundElements}>
          <div className={styles.floatingOrb1}></div>
          <div className={styles.floatingOrb2}></div>
          <div className={styles.gridPattern}></div>
        </div>

        <header className={styles.brandHeader}>
          <h1 id="scadel-brand-title" className={styles.brandTitle}>
            Scadel Furniture
          </h1>
          <div className={styles.titleUnderline}></div>
          <p className={styles.brandTagline}>
            Crafting Excellence in Commercial Furniture
          </p>
          {/* Main Brand Description */}
          <div className={styles.brandContent}>
            <p className={styles.brandDescription}>
              <strong className={styles.highlight}>Scadel Furniture</strong> is
              your trusted partner for high-quality commercial and office
              furniture solutions. As leading{" "}
              <strong className={styles.highlight}>
                Scadel Furniture manufacturers
              </strong>{" "}
              in India, we specialize in premium office chairs, commercial
              workstations, school furniture, and custom furniture solutions.
              Discover why businesses across India choose{" "}
              <strong className={styles.highlight}>Scadel Furniture</strong> for
              their furniture needs.
            </p>
          </div>
        </header>

        <div className={styles.carouselWrapper}>
          <motion.button
            className={styles.navButton}
            onClick={prevSlide}
            aria-label="Previous"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <div className={styles.arrowIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M15 18L9 12L15 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div className={styles.buttonHalo} />
            </div>
          </motion.button>

          <div
            className={styles.carouselContainer}
            ref={carouselRef}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <AnimatePresence custom={direction} initial={false} mode="wait">
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={carouselVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  duration: 0.8,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
                className={styles.carouselItem}
              >
                <div className={styles.imageContainer}>
                  <motion.img
                    src={initailData[currentIndex]?.models[0]?.picture[0]?.path}
                    alt={initailData[currentIndex]?.title}
                    className={styles.carouselImage}
                    loading="eager"
                    decoding="async"
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                  />
                  <div className={styles.gradientOverlay} />
                  <div className={styles.imageShine} />
                </div>

                <motion.div
                  className={styles.contentWrapper}
                  // @ts-ignore
                  variants={slideVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <div className={styles.contentBackground} />
                  <div className={styles.badge}>Featured Collection</div>
                  <h3 className={styles.seriesTitle}>
                    {initailData[currentIndex]?.title}
                  </h3>
                  <p className={styles.seriesDescription}>
                    {initailData[currentIndex]?.description}
                  </p>
                  <div className={styles.buttonContainer}>
                    <motion.a
                      href={`/series/${initailData[currentIndex]?.id}`}
                      className={styles.primaryButton}
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Explore Collection
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M5 12H19M19 12L12 5M19 12L12 19"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </motion.a>
                    <motion.button
                      className={styles.secondaryButton}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      View Details
                    </motion.button>
                  </div>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </div>

          <motion.button
            className={styles.navButton}
            onClick={nextSlide}
            aria-label="Next"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <div className={styles.arrowIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9 18L15 12L9 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div className={styles.buttonHalo} />
            </div>
          </motion.button>
        </div>

        <div className={styles.indicatorsContainer}>
          {initailData.map((_: any, index: number) => (
            <motion.button
              key={index}
              className={`${styles.indicator} ${
                currentIndex === index ? styles.active : ""
              }`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
            >
              <div className={styles.indicatorProgress} />
            </motion.button>
          ))}
        </div>

        {/* Progress Bar */}
        <div className={styles.progressBar}>
          <motion.div
            className={styles.progressFill}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{
              duration: 5,
              ease: "linear",
              repeat: Infinity,
            }}
          />
        </div>
      </section>{" "}
    </>
  );
};

export default React.memo(SeriesCarousel);
